// "Panduan", the help pill: replays the guidance for the current surface. Same entries and calls as
// the rail-docked TourHelpButton (src/features/onboarding/TourHelpButton.tsx), which the PIB editor and
// export still render in their own toolbars as `variant="toolbar"`.
//
// Carries `data-tour="tour-help-pill"`. Exactly one element may: the top bar renders this only while
// the rail is visible, because the pages that hide the rail (PIB editor, export) bring their own pill.
//
// Below 992px the rail is a drawer, closed while the user is up here, and the menu tour ("Panduan menu
// Lumen") is a tour of the rail. So on a phone or tablet the menu tour opens the drawer and runs inside
// it, closes it for the one step that points at the top bar (language and theme), and closes it when the
// tour ends. Old ran the same tour inside the open drawer, where its pill lived.
import { CircleHelpIcon } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { useLanguage } from '@contexts/LanguageContext';
import { useOptionalTour } from '@/features/onboarding';
import type { TourStage } from '@/features/onboarding/TourProvider';
import { resolveTourCopy } from '@/features/onboarding/tour-copy';
import { GLOBAL_TOUR_ID, tourById } from '@/features/onboarding/tour-registry';
import { useOptionalOnboardingGate } from '@/features/first-login/gate-context';
import { Button } from '@/components/lumen/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/lumen/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/lumen/tooltip';
import type { FrameState } from '../AppFrame';

/** The rail inside the open drawer (AppFrame's Sheet). */
const DRAWER_RAIL = '[data-slot="sheet-content"] [data-slot="sidebar-rail"]';
/** Upper bound on waiting for the drawer; the tour moves on regardless and drops a missing anchor. */
const DRAWER_TIMEOUT_MS = 1500;

/** Polls `read` until it returns something or the timeout passes. */
function waitFor<T>(read: () => T | null): Promise<T | null> {
  return new Promise((resolve) => {
    const deadline = performance.now() + DRAWER_TIMEOUT_MS;
    const tick = () => {
      const value = read();
      if (value !== null || performance.now() > deadline) resolve(value);
      else window.setTimeout(tick, 30);
    };
    tick();
  });
}

/**
 * Resolves once the drawer is fully open (rail mounted, slide-in finished: the tour measures its anchor
 * once) or fully gone (its scrim no longer covers the top bar).
 */
async function drawerSettled(open: boolean) {
  if (!open) {
    await waitFor(() => (document.querySelector(DRAWER_RAIL) ? null : true));
    return;
  }
  const sheet = await waitFor(
    () => document.querySelector(DRAWER_RAIL)?.closest('[data-slot="sheet-content"]') ?? null
  );
  await Promise.allSettled(sheet?.getAnimations().map((animation) => animation.finished) ?? []);
}

/** A step anchored in the top bar needs the drawer closed; every other step of the menu tour is the rail's. */
const isTopbarStep = (anchor: string | undefined) =>
  anchor !== undefined && document.querySelector(`[data-slot="app-topbar"] [data-tour="${anchor}"]`) !== null;

export function HelpMenu({ isDesktop, setMobileOpen }: Pick<FrameState, 'isDesktop' | 'setMobileOpen'>) {
  const tour = useOptionalTour();
  const onboardingGate = useOptionalOnboardingGate();
  const { language } = useLanguage();
  // The stage outlives this render; it reads the frame through a ref so a rotation to desktop mid-tour
  // stops it from opening a drawer that no longer exists.
  const frame = useRef({ isDesktop, setMobileOpen });
  useEffect(() => {
    frame.current = { isDesktop, setMobileOpen };
  });
  // Runs once the menu has closed and handed focus back to the pill, so the drawer records the pill as
  // the place to return focus to.
  const afterMenuClose = useRef<(() => void) | null>(null);
  const pillRef = useRef<HTMLButtonElement>(null);

  if (!tour) return null;

  const { pageTour, subTour, subTourLabel, sections, startTour, startTourAt, startTourById } = tour;
  const globalTour = tourById(GLOBAL_TOUR_ID);
  // Copy resolves with an explicit language, like the tours themselves (docs/onboarding.md).
  const t = (key: string, vars?: Record<string, string | number>) => resolveTourCopy(language, key, vars);
  const hasPageEntries = Boolean(subTour || pageTour);

  const startMenuTourInDrawer = async () => {
    if (!globalTour) return;
    // Open first: the tour keeps the steps whose anchors are on screen when it starts.
    setMobileOpen(true);
    await drawerSettled(true);
    const stage: TourStage = {
      prepare: async (step) => {
        if (frame.current.isDesktop) return;
        const open = !isTopbarStep(step.anchor);
        frame.current.setMobileOpen(open);
        await drawerSettled(open);
      },
      restore: () => {
        frame.current.setMobileOpen(false);
        // The drawer hands focus back to whatever had it when it opened. After Back from the top bar step
        // that was the tour's popover, gone by now, and focus would drop to <body>: fall back to the pill
        // that started the tour.
        void drawerSettled(false).then(() => {
          if (!document.activeElement || document.activeElement === document.body) pillRef.current?.focus();
        });
      },
    };
    startTour(globalTour, 0, stage);
  };

  const startMenuTour = () => {
    if (isDesktop) {
      startTourById(GLOBAL_TOUR_ID);
      return;
    }
    afterMenuClose.current = () => void startMenuTourInDrawer();
  };

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              ref={pillRef}
              variant="outline"
              size="sm"
              data-tour="tour-help-pill"
              aria-label={t('pill.aria')}
              className="h-8 rounded-full px-2 text-fg-2 has-[>svg]:px-2 data-[state=open]:bg-surface-2 data-[state=open]:text-fg xl:px-3 xl:has-[>svg]:px-2.5"
            >
              <CircleHelpIcon aria-hidden />
              <span className="hidden xl:inline">{t('pill.label')}</span>
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        {/* The label is visible from `xl`; below that the icon carries it in a tooltip. */}
        <TooltipContent side="bottom" sideOffset={4} className="xl:hidden">
          {t('pill.label')}
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        align="end"
        className="w-60"
        onCloseAutoFocus={() => {
          // Radix focuses the pill right after this handler; the deferred action's state update renders
          // after that.
          const action = afterMenuClose.current;
          afterMenuClose.current = null;
          action?.();
        }}
      >
        {subTour && (
          <DropdownMenuItem onSelect={() => startTour(subTour)}>
            {t('pill.tabGuide', { tab: subTourLabel ?? '' })}
          </DropdownMenuItem>
        )}
        {pageTour && <DropdownMenuItem onSelect={() => startTour(pageTour)}>{t('pill.pageGuide')}</DropdownMenuItem>}
        {/* Entry points partway into the page's tour, for a user who wants only that part again. */}
        {pageTour &&
          sections.map((section) => (
            <DropdownMenuItem key={section.key} onSelect={() => startTourAt(pageTour, section.stepKey)}>
              {t(`pill.section.${section.key}`)}
            </DropdownMenuItem>
          ))}
        {hasPageEntries && (globalTour || onboardingGate) && <DropdownMenuSeparator />}
        {globalTour && (
          <DropdownMenuItem className="text-fg-2" onSelect={startMenuTour}>
            {t('pill.menuGuide')}
          </DropdownMenuItem>
        )}
        {/* Re-runs the first-login wizard on request, whatever its recorded status. */}
        {onboardingGate && (
          <DropdownMenuItem className="text-fg-2" onSelect={onboardingGate.openOnboarding}>
            {t('pill.onboarding')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
